// Drawer d'édition Contact & Branding - ouvert depuis l'onglet Informations générales

import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Row,
  Col,
  ColorPicker,
  Typography,
  message,
  Alert,
  Button,
  Space,
} from 'antd';
import { BgColorsOutlined } from '@ant-design/icons';
import { useUpdateBusiness } from '../../../hooks/useBusinesses';
import { Business } from '../../../types/business';
import { cleanFirestoreData } from '../../../utils/cleanFirestoreData';
import BrandingImageUpload from '../../../components/shared/BrandingImageUpload';
import {
  uploadBusinessBrandingAsset,
  deleteBusinessBrandingAssetByUrl,
} from '../../../hooks/useBusinessBrandingAssets';

const { TextArea } = Input;
const { Text } = Typography;

interface EditContactBrandingModalProps {
  open: boolean;
  business: Business | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditContactBrandingModal: React.FC<EditContactBrandingModalProps> = ({
  open,
  business,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const updateBusinessMutation = useUpdateBusiness();
  const [logoUrl, setLogoUrl] = useState<string | undefined>();
  const [logoDirty, setLogoDirty] = useState(false);
  const [stampUrl, setStampUrl] = useState<string | undefined>();
  const [stampDirty, setStampDirty] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState<string | undefined>();
  const [signatureDirty, setSignatureDirty] = useState(false);

  useEffect(() => {
    if (business && open) {
      const branding = business.branding;
      setLogoUrl(branding?.logo);
      setLogoDirty(false);
      setStampUrl(branding?.companyStampUrl);
      setStampDirty(false);
      setSignatureUrl(branding?.companySignatureUrl);
      setSignatureDirty(false);
      form.setFieldsValue({
        smsSenderName: branding?.smsSenderName || '',
        phone: branding?.phone || '',
        whatsappNumber: branding?.whatsappNumber || '',
        address: branding?.address || '',
        description: branding?.description || '',
        primaryColor: branding?.primaryColor || '#667eea',
        secondaryColor: branding?.secondaryColor || '#764ba2',
      });
    }
  }, [business, open, form]);

  const handleSubmit = async () => {
    if (!business) return;
    try {
      const values = await form.validateFields();
      const primaryColor =
        values.primaryColor instanceof Object
          ? values.primaryColor.toHexString()
          : values.primaryColor || '#667eea';
      const secondaryColor =
        values.secondaryColor instanceof Object
          ? values.secondaryColor.toHexString()
          : values.secondaryColor || '#764ba2';

      const brandingData: any = {
        ...business.branding,
        smsSenderName: values.smsSenderName?.trim() || business.branding?.smsSenderName || '',
        phone: values.phone?.trim() || business.branding?.phone || '',
        whatsappNumber: values.whatsappNumber?.trim() || business.branding?.whatsappNumber || '',
        address: values.address?.trim() || business.branding?.address || '',
        primaryColor,
        secondaryColor,
      };
      if (logoDirty) {
        if (logoUrl) {
          brandingData.logo = logoUrl;
        } else {
          delete brandingData.logo;
        }
      }
      if (stampDirty) {
        if (stampUrl) {
          brandingData.companyStampUrl = stampUrl;
        } else {
          delete brandingData.companyStampUrl;
        }
      }
      if (signatureDirty) {
        if (signatureUrl) {
          brandingData.companySignatureUrl = signatureUrl;
        } else {
          delete brandingData.companySignatureUrl;
        }
      }
      const desc = values.description?.trim() || business.branding?.description;
      if (desc) brandingData.description = desc;

      await updateBusinessMutation.mutateAsync({
        id: business.id,
        branding: cleanFirestoreData(brandingData),
      });
      message.success('Contact et branding mis à jour');
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  if (!business) return null;

  return (
    <Drawer
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BgColorsOutlined style={{ color: '#722ed1' }} />
          Modifier contact et branding
        </span>
      }
      open={open}
      onClose={handleCancel}
      destroyOnClose
      width={560}
      extra={
        <Space>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button type="primary" loading={updateBusinessMutation.isPending} onClick={handleSubmit}>
            Enregistrer
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Alert
          message="Ces informations sont utilisées pour les SMS, communications client et documents de vente"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {business && (
          <>
          <BrandingImageUpload
            label="Logo entreprise"
            imageUrl={logoUrl}
            hint="PNG, JPG ou WebP — max 500 Ko. Affiché automatiquement sur tickets et factures si présent."
            maxPreviewHeight={64}
            onUpload={async (file) => {
              const url = await uploadBusinessBrandingAsset(business.id, 'logo', file);
              setLogoUrl(url);
              setLogoDirty(true);
            }}
            onRemove={async () => {
              if (logoUrl) {
                try {
                  await deleteBusinessBrandingAssetByUrl(logoUrl);
                } catch {
                  // ignore
                }
              }
              setLogoUrl(undefined);
              setLogoDirty(true);
            }}
          />
          <BrandingImageUpload
            label="Cachet entreprise"
            imageUrl={stampUrl}
            hint="PNG, JPG ou WebP — max 500 Ko. Utilisé sur facture A4 si la feature cachet/signature est active."
            maxPreviewHeight={64}
            onUpload={async (file) => {
              const url = await uploadBusinessBrandingAsset(business.id, 'stamp', file);
              setStampUrl(url);
              setStampDirty(true);
            }}
            onRemove={async () => {
              if (stampUrl) {
                try {
                  await deleteBusinessBrandingAssetByUrl(stampUrl);
                } catch {
                  // ignore
                }
              }
              setStampUrl(undefined);
              setStampDirty(true);
            }}
          />
          <BrandingImageUpload
            label="Signature entreprise"
            imageUrl={signatureUrl}
            hint="PNG, JPG ou WebP — max 500 Ko. Utilisée sur facture A4 si la feature cachet/signature est active."
            maxPreviewHeight={64}
            onUpload={async (file) => {
              const url = await uploadBusinessBrandingAsset(business.id, 'signature', file);
              setSignatureUrl(url);
              setSignatureDirty(true);
            }}
            onRemove={async () => {
              if (signatureUrl) {
                try {
                  await deleteBusinessBrandingAssetByUrl(signatureUrl);
                } catch {
                  // ignore
                }
              }
              setSignatureUrl(undefined);
              setSignatureDirty(true);
            }}
          />
          </>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="smsSenderName"
              label="Nom expéditeur SMS (max 11 caractères)"
              rules={[
                { required: true, message: 'Nom SMS requis' },
                { max: 11, message: 'Maximum 11 caractères' },
                { pattern: /^[A-Z0-9]+$/, message: 'Lettres majuscules et chiffres uniquement' },
              ]}
            >
              <Input placeholder="BELLEEPOQUE" style={{ fontFamily: 'monospace' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="Téléphone"
              rules={[
                { required: true, message: 'Téléphone requis' },
                { pattern: /^[0-9+\s-]+$/, message: 'Format invalide' },
              ]}
            >
              <Input placeholder="+237 6XX XXX XXX" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="whatsappNumber"
              label="WhatsApp"
              rules={[
                { required: true, message: 'WhatsApp requis' },
                { pattern: /^[0-9+\s-]+$/, message: 'Format invalide' },
              ]}
            >
              <Input placeholder="+237 6XX XXX XXX" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="address"
              label="Adresse"
              rules={[
                { required: true, message: 'Adresse requise' },
                { min: 5, message: 'Minimum 5 caractères' },
              ]}
            >
              <Input placeholder="Adresse complète" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="description" label="Description (optionnel)">
              <TextArea rows={3} placeholder="Description du business..." maxLength={500} showCount />
            </Form.Item>
          </Col>
        </Row>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Couleurs</Text>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="primaryColor"
              label="Couleur principale"
              rules={[{ required: true, message: 'Couleur requise' }]}
            >
              <ColorPicker format="hex" showText style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="secondaryColor"
              label="Couleur secondaire"
              rules={[{ required: true, message: 'Couleur requise' }]}
            >
              <ColorPicker format="hex" showText style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <div
          style={{
            height: 48,
            borderRadius: 8,
            background: `linear-gradient(135deg, ${form.getFieldValue('primaryColor') || '#667eea'} 0%, ${form.getFieldValue('secondaryColor') || '#764ba2'} 100%)`,
            marginTop: 8,
          }}
        />
      </Form>
    </Drawer>
  );
};

export default EditContactBrandingModal;
